import paper from "paper";
import jsPDF from "jspdf";
import { TRIAL_WATERMARK } from "../../helpers/trialWatermark";

const triggerDownload = (href, filename) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatStampDate = () => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const d = new Date();
  const date = `${String(d.getDate()).padStart(2, "0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
  let hr = d.getHours();
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${date} · ${hr}:${min} ${ampm}`;
};

const WATERMARK_ANGLE_DEG = -24;
const WATERMARK_EDGE_PAD = 0.88;

const watermarkFontSize = (width, height, scale = 1) =>
  Math.max(28 * scale, Math.min(72 * scale, Math.round(Math.min(width, height) * 0.09)));

// Chord length through the canvas center along the ribbon angle, inset from the edges.
const diagonalFitWidth = (width, height) => {
  const rad = (WATERMARK_ANGLE_DEG * Math.PI) / 180;
  const ux = Math.abs(Math.cos(rad));
  const uy = Math.abs(Math.sin(rad));
  const t = Math.min(
    ux > 1e-6 ? width / 2 / ux : Infinity,
    uy > 1e-6 ? height / 2 / uy : Infinity
  );
  return 2 * t * WATERMARK_EDGE_PAD;
};

const measureWatermarkWidth = (fontSize, text, measureCtx) => {
  const ctx = measureCtx || document.createElement("canvas").getContext("2d");
  ctx.font = `800 ${fontSize}px sans-serif`;
  return ctx.measureText(text).width;
};

// Cap at the size-based default, then shrink so the overlay text fits on the diagonal.
const fitWatermarkFontSize = (width, height, scale = 1, measureCtx) => {
  const text = TRIAL_WATERMARK.overlayText;
  const maxSize = watermarkFontSize(width, height, scale);
  const minSize = Math.max(10 * scale, 8);
  const available = diagonalFitWidth(width, height);
  const measured = measureWatermarkWidth(maxSize, text, measureCtx);
  if (!(measured > 0) || measured <= available) return maxSize;
  return Math.max(minSize, Math.floor(maxSize * (available / measured)));
};

// Paint the trial ribbon diagonally across the export.
const drawWatermarkOnCanvas = async (ctx, width, height, scale = 1) => {
  ctx.save();
  ctx.globalAlpha = TRIAL_WATERMARK.opacity;
  ctx.translate(width / 2, height / 2);
  ctx.rotate((WATERMARK_ANGLE_DEG * Math.PI) / 180);
  ctx.font = `800 ${fitWatermarkFontSize(width, height, scale, ctx)}px sans-serif`;
  ctx.fillStyle = "#a4243b";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(TRIAL_WATERMARK.overlayText, 0, 0);
  ctx.restore();
};

// Inject the trial ribbon into an SVG export string.
const applyWatermarkToSvg = async (svg, viewWidth, viewHeight) => {
  const fontSize = fitWatermarkFontSize(viewWidth, viewHeight, 1);
  const ribbon = `<g transform="translate(${viewWidth / 2} ${viewHeight / 2}) rotate(${WATERMARK_ANGLE_DEG})"><text x="0" y="0" font-family="sans-serif" font-size="${fontSize}" font-weight="800" fill="#a4243b" fill-opacity="${TRIAL_WATERMARK.opacity}" text-anchor="middle" dominant-baseline="middle">${TRIAL_WATERMARK.overlayText}</text></g>`;
  return svg.replace(/<\/svg>\s*$/, `${ribbon}</svg>`);
};

const downloadSvg = async (filename, { watermark = false } = {}) => {
  let svg = paper.project.exportSVG({ asString: true });
  if (watermark) {
    const view = paper.view;
    const size = view && view.viewSize ? view.viewSize : { width: 1200, height: 800 };
    svg = await applyWatermarkToSvg(svg, size.width, size.height);
  }
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${filename}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Render the current paper.js scene into a fresh canvas at the given pixel
// dimensions, then hand it to the caller. Used for raster exports (PNG/JPEG/PDF).
const rasterize = (background = "white", { watermark = true } = {}) => {
  const svg = paper.project.exportSVG({ asString: true });
  const view = paper.view;
  const size = view && view.viewSize ? view.viewSize : { width: 1200, height: 800 };
  const scale = 2;
  const width = Math.max(1, Math.round(size.width * scale));
  const height = Math.max(1, Math.round(size.height * scale));

  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      if (watermark) {
        try {
          await drawWatermarkOnCanvas(ctx, width, height, scale);
        } catch (e) {
          // Non-fatal: deliver the export without the stamp if it fails.
        }
      }
      resolve({ canvas, width, height });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

const downloadPng = async (filename, { watermark = false } = {}) => {
  const { canvas } = await rasterize("white", { watermark });
  const dataUrl = canvas.toDataURL("image/png");
  triggerDownload(dataUrl, `${filename}.png`);
};

const downloadJpeg = async (filename, { watermark = false } = {}) => {
  const { canvas } = await rasterize("white", { watermark });
  const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
  triggerDownload(dataUrl, `${filename}.jpg`);
};

const downloadPdf = async (filename, { watermark = false } = {}) => {
  const { canvas, width, height } = await rasterize("white", { watermark });
  const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
  const orientation = width >= height ? "landscape" : "portrait";
  const pdf = new jsPDF({ orientation, unit: "pt", format: [width, height] });
  pdf.addImage(dataUrl, "JPEG", 0, 0, width, height);
  pdf.save(`${filename}.pdf`);
};

export const exportGraph = async (format, filename = "export", options = {}) => {
  switch ((format || "svg").toLowerCase()) {
    case "png":
      return downloadPng(filename, options);
    case "jpeg":
    case "jpg":
      return downloadJpeg(filename, options);
    case "pdf":
      return downloadPdf(filename, options);
    case "svg":
    default:
      return downloadSvg(filename, options);
  }
};

export const EXPORT_FORMATS = ["SVG", "PNG", "JPEG", "PDF"];

// Returns a PNG data URL of the current paper.js scene without triggering a
// download. Used by the feedback flow to attach the current graph to an email.
export const getGraphPngDataUrl = async ({ watermark = false } = {}) => {
  const { canvas } = await rasterize("white", { watermark });
  return canvas.toDataURL("image/png");
};

// Returns a base64-encoded SVG data URL of the current paper.js scene.
// SVG is tiny for vector line art (typically 5-20KB) and renders crisply at
// any size. Most modern email clients (Gmail web, Apple Mail) render inline
// SVG via <img src="data:image/svg+xml;base64,..."> fine; Outlook desktop is
// inconsistent. Use as the preferred email-embed path with a JPEG fallback.
export const getGraphSvgDataUrl = () => {
  const svg = paper.project.exportSVG({ asString: true });
  // btoa() can't handle UTF-8 directly; round-trip via encodeURIComponent.
  const base64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64}`;
};

// Render the current scene once into an Image we can re-encode multiple times.
const loadSceneAsImage = () => {
  const svg = paper.project.exportSVG({ asString: true });
  const view = paper.view;
  const size = view && view.viewSize ? view.viewSize : { width: 1200, height: 800 };
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ img, naturalWidth: size.width, naturalHeight: size.height });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

const encodeJpeg = (img, width, height, quality) => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
};

// Iteratively shrink dimensions / quality until the JPEG data URL fits within
// `maxBytes`. EmailJS v3 templates cap total params at ~50KB on the free tier,
// so we leave ~15KB headroom for the other fields. Returns `""` if even the
// smallest fallback can't fit — caller can choose to send without the image.
export const getGraphCompactJpegDataUrl = async (maxBytes = 35 * 1024) => {
  const { img, naturalWidth, naturalHeight } = await loadSceneAsImage();
  const aspect = naturalWidth / naturalHeight;

  const attempts = [
    { longest: 1200, quality: 0.78 },
    { longest: 1000, quality: 0.72 },
    { longest: 800, quality: 0.7 },
    { longest: 700, quality: 0.65 },
    { longest: 600, quality: 0.6 },
    { longest: 500, quality: 0.55 },
    { longest: 400, quality: 0.5 },
  ];

  for (const { longest, quality } of attempts) {
    let w, h;
    if (naturalWidth >= naturalHeight) {
      w = Math.min(longest, naturalWidth);
      h = w / aspect;
    } else {
      h = Math.min(longest, naturalHeight);
      w = h * aspect;
    }
    const dataUrl = encodeJpeg(img, w, h, quality);
    if (dataUrl.length <= maxBytes) return dataUrl;
  }
  // Out of options — return the smallest attempt anyway so the caller can
  // decide whether to send without it or surface a clear error.
  const last = attempts[attempts.length - 1];
  let w, h;
  if (naturalWidth >= naturalHeight) {
    w = Math.min(last.longest, naturalWidth);
    h = w / aspect;
  } else {
    h = Math.min(last.longest, naturalHeight);
    w = h * aspect;
  }
  return encodeJpeg(img, w, h, last.quality);
};
